<template lang="pug">
.privacy-policy
  .privacy-policy__inner
    header.privacy-policy__header
      p.privacy-policy__eyebrow Aplicación de uso interno
      h1 Política de Privacidad
      p.privacy-policy__meta
        | Aplicación: #[strong Grupo Piñón]
        br
        | Desarrollador: #[strong Controlla]
        br
        | Última actualización: 23 de julio de 2026

    section
      h2 1. Introducción
      p
        | Esta Política de Privacidad describe cómo Controlla (“nosotros”, “nuestro”) trata la información
        | en relación con la aplicación móvil #[strong Grupo Piñón] (la “Aplicación”), destinada
        | exclusivamente a la gestión operativa interna de la empresa (inventario, recepción, envíos,
        | transferencias y procesos relacionados).
      p
        | Al utilizar la Aplicación, usted acepta las prácticas descritas en esta Política. Si no está de
        | acuerdo, no debe utilizar la Aplicación.

    section
      h2 2. Naturaleza de la Aplicación (uso interno)
      p
        | La Aplicación #[strong no está dirigida al público general]. El acceso está restringido a
        | personal autorizado de la organización. Ninguna persona puede crear una cuenta por sí misma ni
        | utilizar la Aplicación sin haber sido previamente habilitada por un administrador en el
        | sistema principal (backoffice).
      ul
        li El alta, modificación y baja de usuarios la realiza un administrador autorizado.
        li Las credenciales son personales e intransferibles.
        li El uso indebido o el acceso no autorizado puede derivar en la suspensión de la cuenta y
          | en las medidas internas que corresponda.

    section
      h2 3. Responsable del tratamiento
      p
        | El responsable del tratamiento de los datos personales asociados al uso de la Aplicación es
        | la organización que opera el sistema Grupo Piñón, con el soporte técnico de Controlla como
        | desarrollador de la plataforma.
      p
        | Contacto para temas de privacidad:
        br
        | Correo:
        a(href="mailto:asalaices@controlla.com.mx")  asalaices@controlla.com.mx
        br
        | Sitio web:
        a(href="https://controlla.com.mx" target="_blank" rel="noopener noreferrer")  https://controlla.com.mx

    section
      h2 4. Datos que recopilamos
      p
        | Recopilamos únicamente la información necesaria para autenticar usuarios autorizados y
        | operar los procesos de negocio internos. Según el uso de la Aplicación, puede incluir:

      h3 4.1 Datos de cuenta y autenticación
      ul
        li Nombre
        li Correo electrónico corporativo o asignado
        li Contraseña (almacenada de forma cifrada / no reversible en el servidor)
        li Identificadores de sesión (por ejemplo, token JWT)
        li Sucursal, rol y permisos asignados por el administrador

      h3 4.2 Datos operativos de negocio
      ul
        li Información de inventario, productos, ubicaciones y etiquetas
        li Datos de recepciones, pedidos, envíos, transferencias y documentos relacionados
        li Registros de actividad necesarios para auditoría operativa (quién realizó qué acción y cuándo)

      h3 4.3 Datos técnicos del dispositivo
      ul
        li Tipo de dispositivo, sistema operativo y versión de la Aplicación
        li Identificadores técnicos necesarios para el funcionamiento seguro de la sesión
        li Registros de error o diagnóstico, cuando se generen para soporte técnico

      h3 4.4 Permisos del dispositivo
      p
        | La Aplicación puede solicitar permisos del sistema operativo únicamente para funciones
        | operativas. Según la versión y las funciones habilitadas, esto puede incluir:
      ul
        li
          strong Cámara:
          |  para escanear códigos de barras / QR asociados a mercancía o documentos.
        li
          strong Red / Internet:
          |  para autenticarse y sincronizar información con los servidores de la empresa.
        li
          strong Almacenamiento (si aplica):
          |  para guardar temporalmente archivos o capturas necesarias para el flujo operativo.

      p
        | No solicitamos permisos de ubicación, contactos, micrófono, calendario ni otros datos
        | personales ajenos a la operación, salvo que una función específica lo requiera y se informe
        | en la propia solicitud del sistema operativo.
      p
        | Usted puede denegar o revocar permisos desde la configuración del dispositivo; algunas
        | funciones podrían dejar de estar disponibles.

    section
      h2 5. Finalidades del tratamiento
      p Utilizamos la información para:
      ul
        li Autenticar usuarios autorizados y mantener la seguridad de la sesión
        li Permitir la gestión interna de inventario y operaciones logísticas
        li Asignar accesos según roles y sucursales definidos por el administrador
        li Auditar operaciones, prevenir fraude o uso indebido y dar soporte técnico
        li Mejorar la estabilidad y el rendimiento de la Aplicación
        li Cumplir obligaciones legales o requerimientos de autoridad competentes, cuando proceda

      p
        | #[strong No] utilizamos los datos para publicidad dirigida, marketing a terceros ni creación
        | de perfiles con fines comerciales externos.

    section
      h2 6. Base del tratamiento
      p El tratamiento se fundamenta en:
      ul
        li La relación laboral / contractual / de colaboración con la organización
        li La necesidad de ejecutar las funciones internas para las que se otorga el acceso
        li El interés legítimo de la organización en asegurar, auditar y operar sus sistemas internos
        li El cumplimiento de obligaciones legales aplicables

    section
      h2 7. Compartición de datos
      p
        | #[strong No vendemos] datos personales. La información se trata dentro del entorno empresarial
        | y de los proveedores tecnológicos estrictamente necesarios para operar el servicio (por
        | ejemplo, hosting, infraestructura cloud o herramientas de monitoreo), bajo medidas de
        | confidencialidad y seguridad adecuadas.
      p Podemos compartir información únicamente cuando:
      ul
        li Sea necesario para el funcionamiento técnico de la plataforma (proveedores de servicio)
        li Lo exija la ley, un proceso judicial o una autoridad competente
        li Sea necesario para proteger derechos, seguridad o integridad de la organización o de los usuarios

      p La información no se comparte con redes publicitarias ni plataformas de analítica de marketing.

    section
      h2 8. Transferencias internacionales
      p
        | Si la infraestructura o algún proveedor se encuentra fuera del país donde se opera la
        | Aplicación, la información podrá procesarse en dichos lugares únicamente para las finalidades
        | descritas, aplicando salvaguardas razonables de seguridad y confidencialidad.

    section
      h2 9. Conservación de los datos
      p Conservamos la información mientras:
      ul
        li La cuenta del usuario permanezca activa o sea necesaria para la operación
        li Existan obligaciones legales, contables, de auditoría o de seguridad que lo requieran
        li Sea necesario para resolver disputas o hacer valer derechos

      p
        | Cuando un administrador desactive o elimine un usuario, el acceso a la Aplicación cesa.
        | Ciertos registros operativos pueden conservarse por políticas internas de retención y
        | auditoría.

    section
      h2 10. Seguridad
      p Implementamos medidas técnicas y organizativas razonables, entre ellas:
      ul
        li Autenticación mediante credenciales y tokens de acceso
        li Transmisión de datos a través de canales cifrados (HTTPS), cuando la red lo permita
        li Control de acceso basado en roles y habilitación administrativa previa
        li Prácticas de almacenamiento seguro de contraseñas en el servidor

      p
        | Ningún sistema es 100 % seguro. Usted debe proteger sus credenciales, no compartirlas y
        | notificar de inmediato cualquier sospecha de acceso no autorizado.

    section
      h2 11. Menores de edad
      p
        | La Aplicación está diseñada para uso laboral / empresarial por personal autorizado y
        | #[strong no está dirigida a menores de 13 años] (ni, en general, a menores de edad). No
        | recopilamos de forma consciente información de menores. Si tiene conocimiento de que un menor
        | obtuvo acceso, contacte a la organización o a Controlla para solicitar la eliminación del
        | acceso y de los datos correspondientes.

    section
      h2 12. Derechos del usuario
      p
        | Dado el carácter interno de la Aplicación, la gestión de cuentas y datos personales se
        | realiza principalmente a través del administrador del sistema. Usted puede solicitar:
      ul
        li Acceso a la información de su cuenta
        li Corrección de datos inexactos
        li Desactivación o eliminación de su acceso (sujeto a políticas internas y obligaciones legales)
        li Información sobre el tratamiento de sus datos

      p
        | Para ejercer estos derechos, contacte primero al administrador interno de su organización o
        | escriba a
        a(href="mailto:asalaices@controlla.com.mx")  asalaices@controlla.com.mx
        | .

    section
      h2 13. Servicios de terceros y tiendas de aplicaciones
      p
        | La distribución de la Aplicación a través de Google Play o Apple App Store está sujeta a las
        | políticas de esas plataformas. Esta Política se refiere al tratamiento de datos por parte de
        | la Aplicación y de Controlla / la organización operadora; no regula el tratamiento independiente
        | que realicen Google, Apple u otros terceros fuera de nuestro control.

    section
      h2 14. Retención de datos en el dispositivo
      p
        | La Aplicación puede almacenar localmente información mínima necesaria para mantener la sesión
        | (por ejemplo, el token de acceso) y mejorar la experiencia de uso. Al cerrar sesión, el token
        | se invalida en el servidor y debe eliminarse del dispositivo. La desinstalación de la
        | Aplicación elimina los datos locales asociados, salvo lo que conserve el sistema operativo.

    section
      h2 15. Cambios a esta Política
      p
        | Podemos actualizar esta Política de Privacidad para reflejar cambios legales, técnicos u
        | operativos. La fecha de “Última actualización” al inicio indica la versión vigente. El uso
        | continuado de la Aplicación después de una actualización implica el conocimiento de los
        | cambios. Cuando los cambios sean relevantes, se procurará comunicar la actualización por
        | canales internos razonables.

    section
      h2 16. Contacto
      p Para dudas, solicitudes o reportes relacionados con privacidad:
      ul
        li
          | Correo:
          a(href="mailto:asalaices@controlla.com.mx")  asalaices@controlla.com.mx
        li
          | Web:
          a(href="https://controlla.com.mx" target="_blank" rel="noopener noreferrer")  https://controlla.com.mx

      p.privacy-policy__footer
        | © {{ currentYear }} Controlla / Grupo Piñón. Todos los derechos reservados.
        br
        | Documento destinado a cumplir con los requisitos de transparencia de Google Play y Apple App Store
        | para aplicaciones de uso interno / empresarial.
</template>

<script>
export default {
  name: 'PrivacyPolicy',
  computed: {
    currentYear () {
      return new Date().getFullYear()
    }
  }
}
</script>

<style lang="scss" scoped>
.privacy-policy {
  min-height: 100vh;
  background: #f7f8fa;
  color: #1a1a1a;
  padding: 32px 16px 64px;
}

.privacy-policy__inner {
  max-width: 760px;
  margin: 0 auto;
  background: #ffffff;
  border: 1px solid #e6e8ec;
  border-radius: 12px;
  padding: 32px 28px 40px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
}

.privacy-policy__header {
  margin-bottom: 28px;
  padding-bottom: 20px;
  border-bottom: 1px solid #eef0f3;
}

.privacy-policy__eyebrow {
  display: inline-block;
  margin: 0 0 12px;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: #0b5cab;
  background: #eaf3fc;
  border-radius: 6px;
}

h1 {
  margin: 0 0 12px;
  font-size: 28px;
  line-height: 1.25;
  font-weight: 700;
}

h2 {
  margin: 28px 0 10px;
  font-size: 18px;
  line-height: 1.35;
  font-weight: 700;
}

h3 {
  margin: 16px 0 8px;
  font-size: 15px;
  font-weight: 600;
}

.privacy-policy__meta,
p,
li {
  font-size: 14.5px;
  line-height: 1.65;
  color: #333;
}

p {
  margin: 0 0 12px;
}

ul {
  margin: 0 0 14px;
  padding-left: 20px;
}

li {
  margin-bottom: 6px;
}

a {
  color: #0b5cab;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
}

.privacy-policy__footer {
  margin-top: 28px;
  padding-top: 18px;
  border-top: 1px solid #eef0f3;
  color: #667085;
  font-size: 13px;
}

@media (max-width: 576px) {
  .privacy-policy {
    padding: 12px 10px 40px;
    background: #ffffff;
  }

  .privacy-policy__inner {
    border: none;
    border-radius: 0;
    box-shadow: none;
    padding: 16px 8px 24px;
  }

  h1 {
    font-size: 24px;
  }
}
</style>
